import React, { useState, useEffect, useRef, useCallback } from "react";
import { Send, X, MessageCircle, Loader2 } from "lucide-react";
import "../Styling/Chatbot.css";
const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const messagesEndRef = useRef(null);

  const API_BASE_URL = "https://ddw-chatbot.vercel.app/api";

  const generateUUID = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  };

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, []);

  const checkSessionAndLoadHistory = useCallback(async () => {
    try {
      setIsLoadingHistory(true);

      const sessionResponse = await fetch(
        `${API_BASE_URL}/chat/check-session/${userId}`,
        {
          method: "POST",
        }
      );

      const sessionData = await sessionResponse.json();

      if (sessionData.action === "archived") {
        setMessages([]);
        setIsLoadingHistory(false);
        return;
      }

      const historyResponse = await fetch(
        `${API_BASE_URL}/chat/history/${userId}`
      );
      const historyData = await historyResponse.json();

      if (historyData.messages && historyData.messages.length > 0) {
        setMessages(historyData.messages);
      }
      setTimeout(scrollToBottom, 0);

      setIsLoadingHistory(false);
    } catch (error) {
      console.error("Error loading chat:", error);
      setIsLoadingHistory(false);
    }
  }, [userId, API_BASE_URL, scrollToBottom]);

  useEffect(() => {
    let storedUserId = localStorage.getItem("chat_user_id");

    if (!storedUserId) {
      storedUserId = generateUUID();
      localStorage.setItem("chat_user_id", storedUserId);
    }

    setUserId(storedUserId);
  }, []);

  useEffect(() => {
    if (userId) {
      checkSessionAndLoadHistory();
    }
  }, [userId, checkSessionAndLoadHistory]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToBottom, 0);
    }
  }, [isOpen, scrollToBottom]);

  const cleanResponse = (text) => {
    // Parse markdown-style formatting and URLs

    // Regex patterns
    const boldPattern = /\*\*(.*?)\*\*/g;
    const urlPattern = /(https?:\/\/[^\s]+)/g;

    // Create a temporary element to safely handle HTML
    let processedText = text;

    // Replace bold
    processedText = processedText.replace(boldPattern, "<strong>$1</strong>");

    // Replace italic (but not if it's part of bold)
    processedText = processedText.replace(
      /(?<!\*)\*(.*?)\*(?!\*)/g,
      "<em>$1</em>"
    );

    // Replace URLs
    processedText = processedText.replace(
      urlPattern,
      '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #4f46e5; text-decoration: underline;">$1</a>'
    );

    return processedText;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");

    const tempUserMsg = {
      sender: "user",
      message: userMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, tempUserMsg]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          message: userMessage,
        }),
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const botMessage = {
        sender: "bot",
        message: data.response,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error sending message:", error);

      const errorMsg = {
        sender: "bot",
        message: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      {/* Chatbot Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="chatbot-button"
          aria-label="Open chat"
        >
          <MessageCircle size={24} />
          <span className="chatbot-button-text">Ask DDW Assistant</span>
        </button>
      )}

      {/* Chatbot Widget */}
      {isOpen && (
        <div className="chatbot-widget">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-content">
              <MessageCircle size={20} />
              <div>
                <h3 className="chatbot-header-title">DDW Assistant</h3>
                <p className="chatbot-header-subtitle">
                  Typically replies instantly
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="chatbot-close-btn"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages Container */}
          <div className="chatbot-messages">
            {isLoadingHistory ? (
              <div className="chatbot-loader">
                <Loader2 className="chatbot-loader-icon" />
                <p className="chatbot-loader-text">Loading chat history...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="chatbot-welcome">
                <MessageCircle size={40} className="chatbot-welcome-icon" />
                <h4 className="chatbot-welcome-title">Hello! 👋</h4>
                <p className="chatbot-welcome-text">
                  How can I help you today?
                </p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  className={`chatbot-message chatbot-message-${msg.sender}`}
                >
                  <div className="chatbot-message-bubble">
                    <p
                      className="chatbot-message-text"
                      dangerouslySetInnerHTML={{
                        __html: cleanResponse(msg.message),
                      }}
                    />
                    <span className="chatbot-message-time">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
              ))
            )}

            {isLoading && (
              <div className="chatbot-message chatbot-message-bot">
                <div className="chatbot-message-bubble">
                  <div className="chatbot-typing">
                    <span className="chatbot-typing-dot"></span>
                    <span className="chatbot-typing-dot"></span>
                    <span className="chatbot-typing-dot"></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="chatbot-input-area">
            <div className="chatbot-input-wrapper">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask DDW Assistant anything..."
                disabled={isLoading || isLoadingHistory}
                className="chatbot-input"
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputMessage.trim() || isLoadingHistory}
                className="chatbot-send-btn"
                aria-label="Send message"
              >
                <Send size={18} />
              </button>
            </div>
            <p className="chatbot-disclaimer">
              DDW Assistant is here to help answer your queries.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
